import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance";
import { setPollingProgress, setRoomNameMapping } from "../features/graph/graph.slice";

interface graphDataInterface {
  corridor: boolean;
  dimensioned: boolean;
  dimensionedCirculation: boolean;
  non_adj: boolean;
  minDimEnabled: boolean;
  removeAddCirculation: boolean;
  publicEnabled: boolean;
  normalizeConst: boolean;
  limit: number;
  corridorThickness: number;
  starting_from: number;
  count: number;
  rectangular: boolean;
  nodes: { id: number; x: number; y: number; label: string; color: string }[];
  edges: { source: number; target: number; color: string }[];
  plot_width: number | null;
  plot_height: number | null;
}

/**
 * Poll for task result with timeout.
 * Calls GET /api/task/{task_id}/ every `interval` ms until success/failure or timeout.
 */
export async function pollForTaskResult(
  taskId: string,
  onProgress?: (progress: {
    current: number;
    total: number;
    description: string;
  }) => void,
  timeout = 300000, // 5 minutes
  interval = 2000, // 5 seconds
): Promise<any> {
  const url = `${process.env.REACT_APP_BASEURL}/api/task/${taskId}/`;
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const response = await axiosInstance.get(url);

    const data = response.data;
    const status = data.status?.toLowerCase();

    if (response.status === 200) {
      // Task completed successfully
      console.log("Task completed successfully:", taskId);
      return data;
    }

    if (status === "failure") {
      const errorMsg =
        data.error || data.message || "Floorplan generation failed";
      console.error("Task failed:", errorMsg);
      throw new Error(errorMsg);
    }

    // Task still pending or in progress
    if (status === "progress" && data.result) {
      console.log(
        `Task progress: ${data.result.current}/${data.result.total} - ${data.result.description}`,
      );
      onProgress?.({
        current: data.result.current ?? 0,
        total: data.result.total ?? 100,
        description: data.result.description ?? "Processing...",
      });
    } else {
      console.log(`Task status: ${status}, polling again in ${interval}ms...`);
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  throw new Error(
    "Floorplan generation timed out. Please try again.",
  );
}


function normalizeFloorPlanItem(room: any): any {
  const normalizedRoom = {
    ...room,
    _id: room._id || room.id || `room-${Math.random().toString(36).substring(2, 9)}`,
    name: room.original_name || room.name, 
  };

  // Ensure each wall has _id
  if (Array.isArray(normalizedRoom.walls)) {
    normalizedRoom.walls = normalizedRoom.walls.map((wall: any) => ({
      ...wall,
      _id: wall._id || wall.id || `wall-${Math.random().toString(36).substring(2, 9)}`,
    }));
  }

  return normalizedRoom;
}

function normalizeFloorPlans(floorPlans: any[][]): any[][] {
  return floorPlans.map((variant: any[]) =>
    variant.map((room: any) => normalizeFloorPlanItem(room))
  );
}

function extractFloorPlans(result: any): any {
  let floorPlans: any[][] | null = null;

  // New task result format: { status: 'success', data: { floorplans: [...] } }
  if (result?.data?.floorplans) {
    const fp = result.data.floorplans;
    // If it's already a nested array (array of floorplan variants), use as-is
    if (Array.isArray(fp) && fp.length > 0 && Array.isArray(fp[0])) {
      floorPlans = fp;
    }
    // If it's a flat array of room objects, wrap it in an outer array as a single variant
    else if (Array.isArray(fp)) {
      floorPlans = [fp];
    }
  }

  // Old synchronous format: { response: { Documents: { floorPlans: [...] } } }
  if (!floorPlans && result?.response?.Documents?.floorPlans) {
    floorPlans = result.response.Documents.floorPlans;
  }

  // Direct floorPlans field (another possible format)
  if (!floorPlans && result?.Documents?.floorPlans) {
    floorPlans = result.Documents.floorPlans;
  }

  if (!floorPlans) {
    console.warn("Unknown floorplan response format:", result);
    return [];
  }

  // Log sample data for debugging
  if (floorPlans.length > 0 && floorPlans[0].length > 0) {
    const sampleRoom = floorPlans[0][0];
    console.log("Sample room from API:", {
      has_id: !!sampleRoom.id,
      has__id: !!sampleRoom._id,
      name: sampleRoom.name,
      wallCount: sampleRoom.walls?.length,
      sampleWall: sampleRoom.walls?.[0]
        ? {
          has_id: !!sampleRoom.walls[0].id,
          has__id: !!sampleRoom.walls[0]._id,
        }
        : "no walls",
    });
  }

  // Normalize _id fields for both rooms and walls
  return normalizeFloorPlans(floorPlans);
}

export const fetchGraphData = createAsyncThunk(
  "graph/fetchGraphData",
  async (
    {
      graphData,
      type,
    }: {
      graphData: graphDataInterface;
      type: string;
    },
    { dispatch, rejectWithValue },
  ) => {
    console.log("graphData before call", graphData);
    if (graphData?.nodes?.length < 3) {
      return;
    }
    try {
      // Step 1: POST to generate endpoint
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_BASEURL}/api/generate/${type}`,
        graphData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      console.log("Generate response:", response.data);

      // Check if the response contains a task_id (new async flow)
      if (response.data?.task_id) {
        const taskId = response.data.task_id;
        console.log("Task started, polling for result. task_id:", taskId);

        // Step 2: Poll for result
        const result = await pollForTaskResult(
          taskId,
          (progress) => {
            dispatch(setPollingProgress(progress));
          },
        );

        // Step 3: Extract and return floorplans
        const floorPlans = extractFloorPlans(result);
        console.log("Extracted floorplans:", floorPlans?.length, "variants");
        const roomNameMapping = result?.room_name_mapping;
        if (roomNameMapping) {
          dispatch(setRoomNameMapping(roomNameMapping));
        }
        return floorPlans;
      }

      // Fallback: Old synchronous response format (direct floorplan data)
      console.log("Received synchronous response (no task_id)");
      const roomNameMapping = response.data?.room_name_mapping;
      if (roomNameMapping) {
        dispatch(setRoomNameMapping(roomNameMapping));
      }
      return extractFloorPlans(response.data);
    } catch (error: any) {
      console.error("Error in fetchGraphData:", error);
      return rejectWithValue(
        error?.message || "Failed to generate floorplans",
      );
    }
  },
);
